/**
 * The free-tier quotas and what the app does about them (spec §18.4): the Durable Object requests of the day decide
 * the rooms' mode, and the admins are told at 70 % and 85 %. Days are UTC days (Cloudflare resets at 00:00 UTC).
 */
export const QUOTAS = {
  /** Durable Object requests per day (Workers Free). */
  doRequestsPerDay: 100_000,
  /** Incoming WebSocket messages billed as one request. */
  wsPerRequest: 20,
  /** Durable Object duration per day; not measurable by a room (P12 Ruling 3). */
  doGbSecondsPerDay: 13_000,
  orgStorageBytes: 8 * 1024 ** 3,
  usageRetentionDays: 400,
} as const;

/** The counters a room writes to `usage_daily` (spec §7.5). */
export type UsageMetrics = { do_ws_in: number; do_connections: number };

export function doRequests(m: UsageMetrics): number {
  return m.do_ws_in / QUOTAS.wsPerRequest + m.do_connections;
}

/** Share of the day's Durable Object requests used, 1 = all of them (can go past 1). */
export function doRatio(m: UsageMetrics): number {
  return doRequests(m) / QUOTAS.doRequestsPerDay;
}

/**
 * normal; economy (≥ 70 %): cursors at 2 Hz, no viewport follow; edits (≥ 90 %): no presence, only edits; suspended
 * (100 %): sync waits for the next UTC day, local edits kept.
 */
export type RoomMode = 'normal' | 'economy' | 'edits' | 'suspended';

export function roomMode(ratio: number): RoomMode {
  if (ratio >= 1) return 'suspended';
  if (ratio >= 0.9) return 'edits';
  if (ratio >= 0.7) return 'economy';
  return 'normal';
}

export const ALERT_LEVELS = [0.7, 0.85] as const;

/** The alert levels `ratio` has crossed that were not sent yet today, lowest first. */
export function alertsDue(ratio: number, sent: readonly number[]): number[] {
  return ALERT_LEVELS.filter((level) => ratio >= level && !sent.includes(level));
}

/** The next 00:00 UTC strictly after `now`. */
export function nextResetAt(now: Date): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
}

/** The next reset as Paris time (01:00 in winter, 02:00 in summer), in the person's language. */
export function resetLabel(now: Date, locale: 'fr' | 'en'): string {
  return new Intl.DateTimeFormat(locale, { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' }).format(nextResetAt(now));
}
